import type { PilotNameType } from '@features/invoices/api/types/invoice.types';

export const DUE_DATE_STATUS = {
  PAST_DUE: 'past-due',
  DUE_SOON: 'due-soon',
} as const;

export type DueDateStatus =
  | (typeof DUE_DATE_STATUS)[keyof typeof DUE_DATE_STATUS]
  | null;

export const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Returns the due date status for a given date string.
 * - 'past-due'  → due date is before today
 * - 'due-soon'  → due date is within the next 7 days (inclusive of today)
 * - null        → due date is more than 7 days away (or date is missing)
 */
export function getDueDateStatus(
  dueDate: string | undefined | null
): DueDateStatus {
  if (!dueDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  if (due < today) return DUE_DATE_STATUS.PAST_DUE;

  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(today.getDate() + 7);

  if (due <= sevenDaysFromNow) return DUE_DATE_STATUS.DUE_SOON;

  return null;
}

export function formatAmount(
  amount: number | string | null,
  currency: string | null
): string {
  if (amount == null) {
    return '';
  }
  const value = typeof amount === 'string' ? amount.trim() : amount;
  if (value === '') {
    return '-';
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return '-';

  if (currency == null) return '';
  return `${NUMBER_FORMATTER.format(parsed)} ${currency}`;
}

export function shouldShowDueDateIcon(
  dueDateStatus: DueDateStatus,
  targetStatus: DueDateStatus,
  outstandingAmount: number | null,
  balanceStatus: string | undefined
): boolean {
  return (
    dueDateStatus === targetStatus &&
    outstandingAmount !== 0 &&
    balanceStatus !== 'Paid'
  );
}

export function formatPilotName(pilot: PilotNameType): string {
  return [pilot.givenName, pilot.middleName, pilot.lastName]
    .filter(
      (part): part is string => typeof part === 'string' && part.trim() !== ''
    )
    .join(' ');
}
